/**
 * `(id)<instanceId>` inside inline code is the agent's way of naming a deployment in
 * prose. Kept as a standalone parser rather than inlined in the markdown renderer so the
 * chart legends in PR #353 can recognise the same marker.
 *
 * The id character class is deliberately narrow — SFDC ids are alphanumeric and ARTESCA
 * instance ids are UUIDs, so anything with a space or slash is ordinary inline code that
 * merely starts with the same three characters.
 */
const INSTANCE_MARKER = /^\(id\)([A-Za-z0-9_-]+)$/;

export const parseInstanceMarker = (text: string): string | null => INSTANCE_MARKER.exec(text.trim())?.[1] ?? null;

/** Unanchored twin of `INSTANCE_MARKER`, for markers mentioned mid-sentence. */
const EMBEDDED_INSTANCE_MARKER = /\(id\)([A-Za-z0-9_-]+)/g;

export const hasInstanceMarker = (text: string): boolean => text.includes('(id)');

export const extractInstanceIds = (text: string): string[] =>
  [...text.matchAll(EMBEDDED_INSTANCE_MARKER)].map((match) => match[1] as string);

export const replaceInstanceMarkers = (text: string, labelOf: (id: string) => string): string =>
  text.replace(EMBEDDED_INSTANCE_MARKER, (_match, id: string) => labelOf(id));

const LEADING_INSTANCE_MARKER = /^\(id\)([A-Za-z0-9_-]+)/;

/** What a chart series says about a deployment: the badge's id, and what the label adds after it. */
export type LegendInstance = { id: string; rest: string };

/** A marker opening a longer label, e.g. `(id)a12 — Warning Alerts`, split from what follows it. */
export const splitLeadingInstanceMarker = (text: string): LegendInstance | null => {
  const match = LEADING_INSTANCE_MARKER.exec(text);
  return match ? { id: match[1] as string, rest: text.slice(match[0].length) } : null;
};

/**
 * Which series name a deployment, keyed by the resolved label the chart knows them by — the two
 * lists are the same series, before and after resolution.
 */
export const legendInstancesOf = <T extends { label: string }>(
  original: T[],
  resolved: T[],
): Record<string, LegendInstance> => {
  const byResource: Record<string, LegendInstance> = {};
  original.forEach((serie, index) => {
    const marker = splitLeadingInstanceMarker(serie.label);
    const resource = resolved[index]?.label;
    if (marker && resource) byResource[resource] = marker;
  });
  return byResource;
};
